/* 
Structure board by groups to prevent users from moving a ticket between groups
*/
import React, { useState } from "react"
import { useAppSelector, useAppDispatch } from "../../hooks/redux-hooks"
import { KanbanBoard, GroupedTickets, GroupByOptionsKey, Status, GroupByElement, Ticket as TicketType, LoadingStatus } from "../../types/common"
import { useGetGroupByElementsQuery } from "../../services/private/groupBy"
import { useUpdateTicketStatusMutation } from "../../services/private/ticket"
import { MAX_COLUMN_LIMIT, PLACEHOLDER_COLOR } from "../../helpers/constants"
import { 
	selectCurrentTicketId,
} from "../../slices/boardSlice"
import {
	toggleShowModal, 
	setModalType, 
	setModalProps, 
} from '../../slices/modalSlice'
import { Ticket } from "../Ticket"
import { IconButton } from "../page-elements/IconButton"
import { IconContext } from "react-icons"
import { IoIosArrowDown as ArrowDown, IoIosArrowUp as ArrowUp } from "react-icons/io";
import { IconArrowDown } from "../icons/IconArrowDown"
import { IconArrowUp } from "../icons/IconArrowUp"
import { LoadingSpinner } from "../LoadingSpinner"
import { addToast } from "../../slices/toastSlice"
import { v4 as uuidv4 } from "uuid"
import { sortStatusByOrder } from "../../helpers/functions"
import { StatusHeader } from "./StatusHeader"
import { useScreenSize } from "../../hooks/useScreenSize"
import { LG_BREAKPOINT } from "../../helpers/constants"
import { LoadingSkeleton } from "../page-elements/LoadingSkeleton"
import { BoardPlaceholder } from "../placeholders/BoardPlaceholder"

type Props = {
	groupedTickets: GroupedTickets
	tickets: Array<TicketType>
	dragStart: (e: React.DragEvent<HTMLDivElement>) => void
	enableDropping: (e: React.DragEvent<HTMLDivElement>) => void  
	board: KanbanBoard
	groupBy: GroupByOptionsKey 
	statusesToDisplay: Array<Status>
	allStatuses: Array<Status>
	boardStyle: Record<string, string>
	boardId: number
	colWidth: number
	addTicketHandler: (statusId: number) => void
	hideStatusHandler: (statusId: number) => void
	hideStatusHandlerLoading: LoadingStatus
}

export const GroupedBoard = ({
	allStatuses, 
	board, 
	colWidth, 
	dragStart,
	enableDropping,
	boardStyle, 
	tickets,
	groupedTickets, 
	groupBy, 
	boardId,
	statusesToDisplay,
	addTicketHandler,
	hideStatusHandler,
	hideStatusHandlerLoading,
}: Props) => {
	const dispatch = useAppDispatch()
	/* object mapping the group by ids to boolean to denote whether the collapse arrow for that section is on/off */
	const [collapseArrows, setCollapseArrows] = useState<Record<string, boolean>>(
		Object.keys(groupedTickets).reduce((acc: Record<string, boolean>, key: string) => {
		acc[key] = false
		return acc
	}, {}))
	// removing overflowX and maxWidth fixes an issue where the status header dropdowns were not showing
	const { overflowX, maxWidth, ...groupedHeaderStyle } = boardStyle
	const [updateTicketStatus, {isLoading: isUpdateTicketStatusLoading}] = useUpdateTicketStatusMutation() 
	const { width, height } = useScreenSize()
	const {data: groupByElements, isLoading, isError} = useGetGroupByElementsQuery({groupBy: groupBy, ids: Object.keys(groupedTickets)})  
	const [dragTicketId, setDragTicketId] = useState<number | null>(null)

	const handleDrop = async (e: React.DragEvent<HTMLDivElement>, groupById: string) => {
		const ticketId = parseInt(e.dataTransfer.getData("text").replace(`group_by_${groupById}_ticket_`, ""))
		const statusId = parseInt(e.currentTarget.id.replace(`group_by_${groupById}_status_`, ""))
		const ticket = board[statusId].find((tId) => tId === ticketId)
		/* 
			if the status column does not contain the ticket, 
			move the ticket into this column. The !isNaN is to detect if the user
			drags the ticket from one swimlane into the other, where the ticket would not be found in that swimlane
			due to having a different group by Id
		*/
		if (!ticket && !isNaN(ticketId)){
			// new endpoint to PATCH update ticket status
			setDragTicketId(ticketId)
			try {
				await updateTicketStatus({ticketId: ticketId, statusId: statusId}).unwrap()
				dispatch(addToast({
	    			id: uuidv4(),
	    			type: "success",
	    			animationType: "animation-in",
	    			message: `Ticket status updated successfully!`,
	    		}))
			}
			catch (e){
				dispatch(addToast({
	    			id: uuidv4(),
	    			type: "failure",
	    			animationType: "animation-in",
	    			message: `Failed to update ticket status.`,
	    		}))
			}
		}
		setDragTicketId(null)
	}

	return (
		<div className = "tw-flex tw-flex-col tw-gap-y-2">
			<div style={groupedHeaderStyle}>
				{statusesToDisplay.map((status: Status, i: number) => {
					return (
						<div
						className = "tw-flex tw-flex-col tw-bg-gray-50"
						>
							<StatusHeader 
								status={status} 
								boardId={boardId}
								numTickets={board[status.id]?.length} 
								addTicketHandler={addTicketHandler}
								hideStatusHandler={hideStatusHandler}
								hideStatusHandlerLoading={hideStatusHandlerLoading}
								dropdownAlignLeft={i === 0}
							/>
						</div>
					)
				})}
			</div>
			{
				!isLoading ? (
					Object.keys(groupedTickets).map((groupById: string) => {
						const groupByElement = groupByElements?.find((element: GroupByElement) => element.id === parseInt(groupById))
						return (
							<div className = "tw-flex tw-flex-col tw-gap-y-2">
								<div style = {boardStyle}>
									<div className = "tw-w-full tw-flex tw-flex-row tw-gap-x-2 tw-pl-2 tw-justify-between lg:tw-justify-self">
										{width <= LG_BREAKPOINT ? (
											<div className = "tw-flex-col tw-flex tw-gap-y-2">
												<p className = "tw-font-bold">{groupByElement?.name}</p>
												<p className = "tw-flex tw-flex-1">{`${Object.values(groupedTickets[groupById]).reduce((acc: number, arr: Array<number>) => 
													acc + arr.length, 0)} issues`}</p>
											</div>
										) : (
											<>
												<p className = "tw-font-bold">{groupByElement?.name}</p>
												<p className = "tw-flex tw-flex-1">{`${Object.values(groupedTickets[groupById]).reduce((acc: number, arr: Array<number>) => 
												acc + arr.length, 0)} issues`}</p>
											</>
										)}
										<IconButton onClick={() => {
											setCollapseArrows({...collapseArrows, [groupById]: !collapseArrows[groupById]})	
										}}>
									    	{
									    		collapseArrows[groupById] ? 
												<IconArrowUp className = "tw-h-4 tw-w-4"/>
												: <IconArrowDown className = "tw-h-4 tw-w-4"/>
									    	}
										</IconButton>
									</div>
								</div>
								{!collapseArrows[groupById] ? (
									<div style = {boardStyle}>
									{
										statusesToDisplay.map((status, i) => {
											return (
												<div 
													className = "tw-flex tw-flex-col tw-bg-gray-50 tw-min-h-[400px] tw-pt-2"
													id = {`group_by_${groupById}_status_${status.id}`} 
													key = {`group_by_${groupById}_status_${status.id}`} 
													onDrop={(e) => {handleDrop(e, groupById)}} 
													onDragOver={enableDropping} 
												>
													<div className = "tw-flex tw-flex-col tw-gap-y-2 tw-px-2 tw-pb-2">
														{
															groupedTickets[groupById][status.id]?.slice(0, status.limit ?? MAX_COLUMN_LIMIT).map((ticketId: number) => {
																const ticket = tickets.find((t: TicketType) => t.id === ticketId)
																return (
																	<div 
																		onClick={(e) => {
																			if (e.defaultPrevented){
																				return
																			}
																			dispatch(toggleShowModal(true))
																			dispatch(setModalType("EDIT_TICKET_FORM"))
																			dispatch(selectCurrentTicketId(ticketId))	
																		}}
																		key = {`group_by_${groupById}_ticket_${ticketId}`} 
																		id = {`group_by_${groupById}_ticket_${ticketId}`}
																		className={"tw-cursor-pointer"}
																		draggable
																		onDragStart={dragStart}
																		>
																		{ticket ? <Ticket 
																			isLoading={dragTicketId != null && dragTicketId === ticket.id && isUpdateTicketStatusLoading}
																			ticket = {ticket}
																			dropdownAlignLeft={i === 0}
																			statusesToDisplay={statusesToDisplay}
																			boardId={boardId}
																		/> : null}
																	</div>
																)
															})
														}
													</div>
												</div>
											)
										})
									}
									</div>
								) : null}
							</div>
						)
					})) : (
						<LoadingSkeleton width = "tw-flex tw-flex-col tw-gap-y-4 tw-w-full" height={"tw-h-[1000px]"}>
							<div className = {`tw-w-32 tw-h-6 ${PLACEHOLDER_COLOR}`}></div>
							<BoardPlaceholder/>
						</LoadingSkeleton>
					)
			}
		</div>
	)
}

